import { computed, type Ref } from "vue";
import { useField } from "vee-validate";
import {
  useFieldValidation,
  useFieldStyles,
  type WithWarning,
} from "@/composables/ui/inputs/useFieldValidation";
import { reach } from "yup";
import {
  isAnsweredValue,
  useCompactScope,
  useSectionFieldRegistration,
} from "@/composables/shared/useCompactScope";

export interface BaseValidatorProps {
  fieldName: string;
  schema?: unknown;
  modelValue?: unknown;
  label?: string;
  hint?: string;
  placeholder?: string;
  variant?:
    | "outlined"
    | "filled"
    | "underlined"
    | "solo"
    | "solo-inverted"
    | "solo-filled"
    | "plain";
  density?: "default" | "comfortable" | "compact";
  hideDetails?: boolean;
  disabled?: boolean;
  readonly?: boolean;
  class?: string;
  required?: boolean;
  transparent?: boolean;
  waitingForBackend?: boolean;
  showValidationIcon?: boolean;
  touched?: boolean;
  autocomplete?: string;
  withWarning?: WithWarning;
}

export const useValidatorField = (props: BaseValidatorProps, isFocused?: Ref<boolean>) => {
  const fieldSchema = computed(() => {
    const s = props.schema as any;
    if (!s) return undefined;

    // Yup schemas expose describe(); object schemas need reach()
    if (typeof s.describe === "function") {
      const d = s.describe();
      if (d?.type === "object") {
        try {
          return reach(s, props.fieldName);
        } catch (error) {
          logError(error, {
            context: { feature: "useValidatorField", step: "fieldSchema" },
          });
          // If the path isn't in the object schema, fall back to undefined
          return undefined;
        }
      }
    }
    // Already a field schema/rule/function
    return s;
  });

  const { value, errorMessage, meta, setErrors, validate, setTouched } = useField(
    () => props.fieldName,
    fieldSchema as any
  );

  // Make withWarning reactive by wrapping in computed
  const withWarningComputed = computed(() => props.withWarning);

  const {
    state,
    icon: validationIcon,
    color: validationColor,
  } = useFieldValidation({
    value,
    errorMessage,
    metaTouched: meta.touched,
    touched: props.touched,
    required: props.required,
    isFocused,
    withWarning: withWarningComputed as Ref<WithWarning | undefined>,
  });
  const { bgColor, classList } = useFieldStyles({
    transparent: props.transparent,
    disabled: props.disabled,
    required: props.required,
    state,
    baseClass: props.class,
  });

  // Warning/error display computed properties
  const displayMessage = computed(() => {
    if (errorMessage.value) return errorMessage.value;

    if (withWarningComputed.value?.test && withWarningComputed.value?.message) {
      return withWarningComputed.value.message;
    }

    return undefined;
  });

  const hasWarningOnly = computed(() => {
    return (
      !errorMessage.value && withWarningComputed.value?.test && withWarningComputed.value?.message
    );
  });

  const isErrorWhileFocused = computed(() => {
    return !!errorMessage.value && isFocused?.value;
  });

  const { isCompact } = useCompactScope();

  useSectionFieldRegistration(
    computed(() => isAnsweredValue(value.value)),
    computed(() => !!errorMessage.value)
  );

  const baseFieldProps = computed(() => ({
    variant: props.variant,
    density: isCompact.value ? "compact" : props.density,
    disabled: props.disabled,
    label: props.label,
    placeholder: props.placeholder,
    hideDetails: isCompact.value ? ("auto" as const) : props.hideDetails,
    bgColor: bgColor.value,
    class: classList.value,
  }));

  return {
    value,
    errorMessage,
    meta,
    state,
    validationIcon,
    validationColor,
    bgColor,
    classList,
    baseFieldProps,
    setErrors,
    validate,
    setTouched,
    warnings: {
      message: displayMessage,
      only: hasWarningOnly,
      focused: isErrorWhileFocused,
    },
  };
};
